import React, { useState } from 'react';
import { CheckIcon, RocketIcon, SparklesIcon, ShieldIcon } from '../components/Icons';
import { NotificationType } from '../App';
import PageWrapper from '../components/PageWrapper';
import AnimatedSection from '../components/AnimatedSection';

interface PricingPageProps {
  showNotification: (message: string, type: NotificationType) => void;
  navigateTo: (page: string) => void;
  initialPrompt?: string | null;
}

type Currency = 'PKR' | 'USD' | 'GBP';

interface Plan {
  name: string;
  price: Record<Currency, string>;
  description: string;
  features: string[];
  popular?: boolean;
  color: string;
  emoji: string;
  tagline: string;
  buttonText: string;
}

const plans: Plan[] = [
  {
    name: 'Starter',
    price: { PKR: '0', USD: '0', GBP: '0' },
    description: 'For learners and hobby projects.',
    features: ['3,000 Tokens per month', '200 Tokens refreshed daily', 'Unlimited Builds & Previews', 'Core AI Engine', 'Codemine Badge on Projects'],
    color: 'green',
    emoji: '',
    tagline: 'A simple way to start creating.',
    buttonText: 'Start Free'
  },
  {
    name: 'Creator',
    price: { PKR: '2,299', USD: '8', GBP: '7' },
    description: 'For individuals and small businesses ready to launch.',
    features: ['12,000 Tokens per month', '400 Tokens refreshed daily', '1 Live Project', 'Free Hosting & Subdomain', 'Backend & API Support', 'Templates Access', 'Codemine Badge on Projects'],
    color: 'yellow',
    emoji: '',
    tagline: 'Turn your idea into a real product.',
    buttonText: 'Choose Creator'
  },
  {
    name: 'Pro',
    price: { PKR: '4,999', USD: '18', GBP: '14' },
    description: 'For freelancers and startups scaling fast.',
    features: ['30,000 Tokens per month', '600 Tokens refreshed daily', '3 Live Projects', 'Priority Build Speed', 'Custom Domains', 'Templates Access', 'Analytics Dashboard', 'No Codemine Badge'],
    popular: true,
    color: 'blue',
    emoji: '',
    tagline: 'The balance of speed, power, and control.',
    buttonText: 'Upgrade to Pro'
  },
  {
    name: 'Business',
    price: { PKR: '14,999', USD: '54', GBP: '43' },
    description: 'For agencies and teams that need scale.',
    features: ['100,000 Tokens per month', '850 Tokens refreshed daily', '10 Live Projects', 'Premium Hosting & Build Priority', 'Team Accounts & Collaboration', 'No Codemine Badge', 'Dedicated Account Manager'],
    color: 'purple',
    emoji: '',
    tagline: 'Enterprise-grade power for your team.',
    buttonText: 'Go Business'
  }
];

const currencySymbols: Record<Currency, string> = {
  PKR: 'PKR',
  USD: '$',
  GBP: '£'
};

const tokenBoosts = [
    { tokens: '2,000', price: { PKR: '399', USD: '2.50', GBP: '2' } },
    { tokens: '7,500', price: { PKR: '999', USD: '6.50', GBP: '5' } },
    { tokens: '20,000', price: { PKR: '2,499', USD: '16', GBP: '13' } },
    { tokens: '50,000', price: { PKR: '5,999', USD: '40', GBP: '32' } },
];

const personalAssistPrice = { PKR: '9,999', USD: '65', GBP: '52' };

const mobileDeploymentPlans = [
    { 
        name: 'Codemine Deploy ⚡',
        subtitle: 'Quick & Simple',
        description: 'Skip the setup. Launch today.',
        price: { PKR: '7,999', USD: '50', GBP: '40' },
        features: ['Android & iOS App Build and Submission', 'Managed Updates and Compliance Checks', 'Hosted under Codemine’s Developer Accounts', 'Includes Codemine Branding'],
        emoji: '',
        color: 'blue'
    },
    {
        name: 'Your Account Deploy 🛡️',
        subtitle: 'Professional & Branded',
        description: 'Your brand. Your control.',
        price: { PKR: '4,999', USD: '35', GBP: '28' },
        features: ['Build and Submission under Your Developer Accounts', 'Support for In-App Purchases & Monetization', 'Fully Branded with Your Publisher Name', 'Ongoing Updates and Compliance Review', 'Requires Apple & Google Developer Accounts'],
        emoji: '',
        color: 'green'
    }
]

const PricingPage: React.FC<PricingPageProps> = ({ showNotification, navigateTo, initialPrompt }) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [currency, setCurrency] = useState<Currency>('PKR');

  const getPrice = (priceObj: Record<Currency, string>) => {
    const monthlyPrice = parseFloat(priceObj[currency].replace(/,/g, ''));
    if (billingCycle === 'yearly') {
        if (monthlyPrice === 0) return '0';
        return (monthlyPrice * 12 * 0.8).toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return priceObj[currency];
  };

  const handleChoosePlan = (planName: string) => {
    if (planName === 'Starter') {
        showNotification(`The Starter plan is free for everyone!`, 'info');
    } else {
        showNotification(`To subscribe to the ${planName} plan, please contact our sales team.`, 'info');
    }
  };

  const handlePurchase = (itemName: string) => {
    showNotification(`To purchase ${itemName}, please contact our sales team.`, 'info');
  };

  const colorVariants: Record<string, { border: string, text: string, bg: string }> = {
      green: { border: 'border-green-400', text: 'text-green-400', bg: 'bg-green-500/10' },
      yellow: { border: 'border-yellow-400', text: 'text-yellow-400', bg: 'bg-yellow-500/10' },
      blue: { border: 'border-sky-400', text: 'text-sky-400', bg: 'bg-sky-500/10' },
      purple: { border: 'border-purple-400', text: 'text-purple-400', bg: 'bg-purple-500/10' },
  }

  return (
    <PageWrapper>
      <div className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {initialPrompt && (
              <AnimatedSection className="mb-8 max-w-3xl mx-auto">
                <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">✨</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-indigo-400 mb-2">Your Idea is Ready!</h3>
                      <p className="text-sm text-[var(--text-secondary)] mb-3">
                        Select a plan below to start building: <span className="italic text-[var(--text-primary)]">"{initialPrompt}"</span>
                      </p>
                      <p className="text-xs text-indigo-400">After choosing a plan, you'll be redirected to the workspace with your prompt ready to go.</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            )}
            <AnimatedSection className="text-center mb-16">
                <h1 className="text-4xl md:text-5xl font-extrabold text-[var(--text-primary)]">
                 Codemine Pricing Plans
                </h1>
                <p className="mt-4 max-w-2xl mx-auto text-lg text-[var(--text-secondary)]">
                Build apps, websites, and games with AI. Start free, scale as you grow.
                </p>
            </AnimatedSection>
        
            <AnimatedSection className="flex flex-col sm:flex-row justify-center items-center gap-x-8 gap-y-4 mb-12">
            <div className="flex items-center space-x-3">
                <span className={`font-medium ${billingCycle === 'monthly' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>Monthly</span>
                <div 
                    className="relative inline-flex items-center cursor-pointer"
                    onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                >
                    <div className="w-14 h-8 bg-black/20 rounded-full border border-[var(--border-color)]"></div>
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-gradient-to-br from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-full transition-transform duration-300 ease-in-out ${billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-0'}`}></div>
                </div>
                <div className="flex items-center space-x-2">
                    <span className={`font-medium ${billingCycle === 'yearly' ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>Yearly</span>
                    <span className="text-xs font-bold text-green-400 bg-green-500/10 px-2 py-1 rounded-full">SAVE 20%</span>
                </div>
            </div>

            <div className="relative">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
                className="appearance-none w-full sm:w-auto bg-black/20 border border-[var(--border-color)] text-[var(--text-primary)] font-semibold text-sm rounded-lg focus:ring-1 focus:ring-[var(--gradient-from)] focus:border-[var(--gradient-from)] block p-2.5 pr-10"
                aria-label="Select currency"
              >
                <option value="PKR">PKR (Pakistani Rupee)</option>
                <option value="USD">USD (US Dollar)</option>
                <option value="GBP">GBP (British Pound)</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-[var(--text-secondary)]">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
              </div>
            </div>
          </AnimatedSection>
        
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {plans.map((plan, i) => (
                    <AnimatedSection key={plan.name} delay={i * 100}>
                        <div className={`glow-card h-full p-6 rounded-xl bg-[var(--panel-bg)] backdrop-blur-sm relative border flex flex-col transition-all duration-300 ${plan.popular ? 'border-[var(--gradient-from)] scale-105' : 'border-[var(--border-color)]'}`}>
                            {plan.popular && (
                                <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-full">MOST POPULAR</div>
                            )}
                            <div className="flex-grow">
                                <h3 className="text-2xl font-bold text-[var(--text-primary)]">{plan.emoji} {plan.name}</h3>
                                <p className="text-sm text-[var(--text-secondary)] mt-1 h-10">{plan.description}</p>
                                <p className="text-4xl font-extrabold my-6">
                                    {currencySymbols[currency]}{getPrice(plan.price)}
                                    <span className="text-base font-medium text-[var(--text-secondary)]">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                                </p>
                                <ul className="space-y-3 text-sm mb-6">
                                {plan.features.map(feature => (
                                    <li key={feature} className="flex items-start">
                                    <CheckIcon />
                                    <span className="ml-3 text-[var(--text-secondary)]">{feature}</span>
                                    </li>
                                ))}
                                </ul>
                                <p className="text-sm italic text-gray-400 mb-6">✨ {plan.tagline}</p>
                            </div>
                            <button
                                onClick={() => handleChoosePlan(plan.name)}
                                className={`w-full py-3 font-semibold rounded-lg transition-colors ${
                                plan.popular 
                                    ? 'bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] text-white hover:opacity-90' 
                                    : 'bg-white/10 text-[var(--text-primary)] hover:bg-white/20'
                                }`}>
                                 {plan.buttonText}
                            </button>
                        </div>
                    </AnimatedSection>
                ))}
            </div>

            <AnimatedSection className="mt-24 text-center">
                <h2 className="text-4xl md:text-5xl font-extrabold">🎟️ Token Boosts</h2>
                <p className="mt-4 max-w-2xl mx-auto text-lg text-[var(--text-secondary)]">Top up anytime, instantly.</p>
            </AnimatedSection>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                {tokenBoosts.map((pack, i) => (
                    <AnimatedSection key={pack.tokens} delay={i * 100}>
                        <div className="glow-card p-6 rounded-xl bg-[var(--panel-bg)] backdrop-blur-sm border border-[var(--border-color)] text-center">
                            <h3 className="text-2xl font-bold">{pack.tokens} Tokens</h3>
                            <p className="text-3xl font-extrabold my-4">{currencySymbols[currency]}{pack.price[currency]}</p>
                            <button onClick={() => handlePurchase(`${pack.tokens} Token Pack`)} className="w-full py-2 font-semibold rounded-lg bg-white/10 text-[var(--text-primary)] hover:bg-white/20">
                                Purchase
                            </button>
                        </div>
                    </AnimatedSection>
                ))}
            </div>

             <AnimatedSection className="mt-24 text-center">
                <h2 className="text-4xl md:text-5xl font-extrabold">🤖 Codey Personal Assist</h2>
                <p className="mt-4 max-w-3xl mx-auto text-lg text-[var(--text-secondary)]">Your AI engineer on demand. Get a complete website, mobile app, or game built for you.</p>
            </AnimatedSection>
            
            <AnimatedSection className="mt-12 max-w-2xl mx-auto">
                <div className="glow-card p-8 rounded-xl bg-[var(--panel-bg)] backdrop-blur-sm border border-amber-400">
                    <h3 className="text-2xl font-bold text-center">Dedicated AI Engineer</h3>
                     <p className="text-4xl font-extrabold my-4 text-center">{currencySymbols[currency]}{personalAssistPrice[currency]} <span className="text-base font-normal text-gray-400">/ project</span></p>
                    <ul className="space-y-3 text-sm my-6">
                        <li className="flex"><CheckIcon /><span className="ml-3 text-[var(--text-secondary)]">Full build and polish to your requirements</span></li>
                        <li className="flex"><CheckIcon /><span className="ml-3 text-[var(--text-secondary)]">Direct updates via WhatsApp</span></li>
                        <li className="flex"><CheckIcon /><span className="ml-3 text-[var(--text-secondary)]">Priority Build Queue</span></li>
                    </ul>
                    <p className="text-sm text-center italic text-gray-400 mb-6">✨ Perfect when you need a finished product without doing the work.</p>
                    <button onClick={() => handlePurchase('Codey Personal Assist')} className="w-full py-3 font-semibold rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90">
                        Get Your Project Built
                    </button>
                </div>
            </AnimatedSection>

            <AnimatedSection className="mt-24 text-center">
                <h2 className="text-4xl md:text-5xl font-extrabold">📱 Mobile Deployment</h2>
            </AnimatedSection>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {mobileDeploymentPlans.map((plan, i) => (
                     <AnimatedSection key={plan.name} delay={i * 100}>
                        <div className={`glow-card h-full flex flex-col p-8 rounded-xl bg-[var(--panel-bg)] backdrop-blur-sm border ${colorVariants[plan.color].border}`}>
                            <div className="flex-grow">
                                <h3 className={`text-2xl font-bold ${colorVariants[plan.color].text}`}>{plan.emoji} {plan.name}</h3>
                                <p className="font-semibold">{plan.subtitle}</p>
                                <p className="text-sm text-gray-400 mt-1 h-12">{plan.description}</p>
                                <p className="text-4xl font-extrabold my-4">{currencySymbols[currency]}{plan.price[currency]} <span className="text-base font-normal text-gray-400">/ month</span></p>
                                <ul className="space-y-3 text-sm my-6">
                                    {plan.features.map(feat => <li key={feat} className="flex"><CheckIcon /><span className="ml-3 text-[var(--text-secondary)]">{feat}</span></li>)}
                                </ul>
                            </div>
                            <p className="text-sm text-center italic text-gray-400 mb-6">✨ {plan.name === 'Codemine Deploy ⚡' ? 'The best choice for long-term growth and monetization.' : 'The best choice for long-term growth and monetization.'}</p>
                            <button onClick={() => handlePurchase(plan.name)} className="w-full py-3 font-semibold rounded-lg bg-white/10 text-[var(--text-primary)] hover:bg-white/20">
                                Choose {plan.subtitle.split(' ')[0]}
                            </button>
                        </div>
                     </AnimatedSection>
                ))}
            </div>

        </div>
      </div>
    </PageWrapper>
  );
};

export default PricingPage;